import { useMemo } from "react"
import { colorMapping, colors } from "../utils/commonFunction"
import { useTheme } from "./ThemeProvider"

/**
 * Chart.js draws to a canvas, so CSS custom properties cannot reach it — the palette has to be
 * handed to the chart options as plain values. These two objects mirror the tokens in
 * src/styles/theme/_tokens.scss.
 *
 * Every LIGHT value is either the literal that was already hard-coded at the call site, or
 * `undefined` where the call site relied on a Chart.js default. Passing `undefined` through to an
 * option is how Chart.js spells "use the default", so adopting this palette changes nothing in
 * light mode.
 */
export const LIGHT_CHART_THEME = {
  isDark: false,
  // Axes
  grid: "rgba(0, 0, 0, 0.1)",
  zeroLine: "black",
  tick: undefined,
  axisTitle: undefined,

  // Text
  dataLabel: "#0A3690",
  dataLabelShadow: "0px 0px 2px rgba(0, 0, 0, 0.5)",
  legendText: undefined,
  cardTitle: "rgba(10, 54, 144, 1)",

  // Surfaces the chart sits on / draws over
  cardBg: "white",
  segmentBorder: "white",
  tooltipBg: undefined,
  tooltipTitle: undefined,
  tooltipBody: undefined,

  // Doughnut centre / in-chart callouts
  centerText: "#0A3690",
  mutedText: "#666",

  // Second colour in the alternating value-label scheme used by the line chart
  dataLabelAlt: "#000",

  // Fallbacks a caller gets when it supplies no series colour of its own
  seriesFallback: "#0A3690",
  seriesFallbackFill: "rgba(10, 54, 144, 0.18)"
}

export const DARK_CHART_THEME = {
  isDark: true,
  // Axes
  grid: "rgba(255, 255, 255, 0.11)",
  zeroLine: "rgba(255, 255, 255, 0.5)",
  tick: "#ffffff",
  axisTitle: "#ffffff",

  // Text — the value labels run as small as 8px, so they use the lifted tint of the brand blue
  // rather than #3b68ff itself, which is too close to the card at that size.
  dataLabel: "#ffffff",
  dataLabelShadow: "0px 0px 3px rgba(0, 0, 0, 0.9)",
  legendText: "#ffffff",
  cardTitle: "#ffffff",

  // Surfaces
  cardBg: "#0d131f",
  segmentBorder: "#0d131f",
  tooltipBg: "rgba(18, 25, 38, 0.97)",
  tooltipTitle: "#ffffff",
  tooltipBody: "#e3ebf9",

  centerText: "#ffffff",
  mutedText: "#e3ebf9",

  dataLabelAlt: "#ffffff",

  seriesFallback: "#3B68FF",
  seriesFallbackFill: "rgba(59, 104, 255, 0.22)"
}

export const getChartTheme = (isDark) => (isDark ? DARK_CHART_THEME : LIGHT_CHART_THEME)

/**
 * Chart palette for the current theme. Because this subscribes to the theme context, any chart
 * using it re-renders — and so re-reads its options — the moment the switch is flipped.
 */
export const useChartTheme = () => {
  const { isDark } = useTheme()
  return useMemo(() => getChartTheme(isDark), [isDark])
}


/**
 * The series palette in utils/commonFunction.js is built for a white card. Most of it is mid or
 * light blue/teal and reads fine either way, but a handful of near-navy entries all but disappear
 * on a dark card — those, and only those, are lifted here. Anything absent from this map is used
 * exactly as authored, in both themes.
 */
export const DARK_SERIES_REMAP = {
  // NOT the brand blue: #3b68ff sits within deltaE 4 of the palette's own #3C63FF at index 1,
  // so a two-series chart (Pune / Jaipur) drew both series in visually the same colour. In light
  // those two separate by lightness - deep navy vs vivid blue - so the dark pair does too.
  "#0A3690": "#5B8CFF",
  // Translucent area fills of the same navy, used by the multi-area chart
  "rgba(10, 54, 144, 0.20)": "rgba(59, 104, 255, 0.22)",
  "rgba(10, 54, 144, 0.18)": "rgba(59, 104, 255, 0.20)",
  "#22458d": "#5B82FF",
  "#39548a": "#6E90E0",
  "#3b5ea6": "#6E93E8",
  "#5472b1": "#8AA6E0"
}

export const remapSeriesColor = (color, isDark) =>
  (isDark && typeof color === "string" && DARK_SERIES_REMAP[color]) || color

const remap = (color) => remapSeriesColor(color, true)

export const DARK_SERIES_COLORS = colors.map(remap)

export const DARK_COLOR_MAPPING = Object.keys(colorMapping).reduce((acc, key) => {
  acc[key] = remap(colorMapping[key])
  return acc
}, {})

/**
 * Themed series colours. In light mode these are the very same array and object the charts
 * already import, so nothing shifts.
 */
export const useSeriesPalette = () => {
  const { isDark } = useTheme()
  return useMemo(
    () =>
      isDark
        ? { colors: DARK_SERIES_COLORS, colorMapping: DARK_COLOR_MAPPING }
        : { colors, colorMapping },
    [isDark]
  )
}

/**
 * Lifts a locally-declared series palette for dark mode. Charts that keep their own colour list
 * (rather than importing the shared one) pass it through here; in light mode the list comes back
 * untouched.
 */
export const useRemappedColors = (palette) => {
  const { isDark } = useTheme()
  return useMemo(() => {
    if (!isDark) return palette
    return Array.isArray(palette)
      ? palette.map((color) => remapSeriesColor(color, true))
      : remapSeriesColor(palette, true)
    // The palettes passed in are literals rebuilt on every render, so comparing by identity would
    // defeat the memo; their contents are static, which makes the serialised form a stable key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark, JSON.stringify(palette)])
}

export default useChartTheme
